import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const LogoReveal = () => {
  const sectionRef = useRef(null);
  const logoContainerRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [isFlipped, setIsFlipped] = useState(true);
  const [isFoundation, setIsFoundation] = useState(false);

  // GSAP Scroll-driven animation
  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Main logo reveal animation on scroll
      gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "top 20%",
          scrub: 1,
        }
      }).fromTo(logoContainerRef.current,
        { 
          scale: 0.85, 
          opacity: 0,
        },
        { 
          scale: 1, 
          opacity: 1,
          duration: 1,
          ease: "power2.out"
        }
      );

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleLogoClick = () => {
    setIsFlipped(!isFlipped);
  };

  const handleVersionToggle = () => {
    setIsFoundation(!isFoundation);
  };

  const getLogoUrl = () => {
    if (isFoundation) {
      return isFlipped 
        ? "https://customer-assets.emergentagent.com/job_44d33726-9765-4e8e-847d-5c309a2dbcb0/artifacts/47g66p6c_IPMI-Foundation-50th-Logo-White.svg"
        : "https://customer-assets.emergentagent.com/job_44d33726-9765-4e8e-847d-5c309a2dbcb0/artifacts/q7fv1hyt_IPMI-Foundation-50th-Logo-Blue.svg";
    } else {
      return isFlipped 
        ? "https://customer-assets.emergentagent.com/job_44d33726-9765-4e8e-847d-5c309a2dbcb0/artifacts/wxlpzswd_IPMI-50th-Logo-White.svg"
        : "https://customer-assets.emergentagent.com/job_44d33726-9765-4e8e-847d-5c309a2dbcb0/artifacts/4lel4rm6_IPMI-50th-Logo-blue.svg";
    }
  };

  return (
    <section className="logo-reveal-section" ref={sectionRef} id="logo-story">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="section-header centered"
        >
          <h2 className="section-title">The Story Behind the Logo</h2>
          <p className="section-description logo-subtitle-effect">
            A design that honors <span className="subtle-gold">50 years</span> of <span className="highlight-gold">connecting</span> the precious metals community
          </p>
        </motion.div>

        {/* Heritage Text Explanation */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="logo-text-explanation"
        >
          <h3>From Legacy to Celebration</h3>
          <p>
            For fifty years, IPMI&apos;s iconic networking ring has stood for connection and collaboration across the precious metals world. As we celebrate this <strong className="highlight-gold">golden jubilee</strong>, that legacy lives on in a flowing golden ribbon that unites the 5 and 0. It symbolizes the ties that bind our global community, past and present. More than a logo, this mark honors our journey and points boldly toward the future we are shaping together.
          </p>
        </motion.div>

        <div className="logo-comparison-grid">
          {/* 50th Anniversary Logo with GSAP Animation */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="logo-card anniversary-logo"
          >
            <div className="logo-card-header">
              <h3>Our 50th Anniversary Logo</h3>
              <span className="logo-year">2026</span>
            </div>

            {/* Version Toggle Switch */}
            <motion.div 
              className="logo-version-toggle"
              initial={{ opacity: 0 }}
              animate={{ opacity: isInView ? 1 : 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <button
                onClick={handleVersionToggle}
                className={`version-button ${!isFoundation ? 'active' : ''}`}
                data-testid="ipmi-version-btn"
              >
                IPMI
              </button>
              <button
                onClick={handleVersionToggle}
                className={`version-button ${isFoundation ? 'active' : ''}`}
                data-testid="foundation-version-btn"
              >
                Foundation
              </button>
            </motion.div>

            {/* Logo Container with clean GSAP-driven reveal */}
            <div 
              ref={logoContainerRef}
              className="logo-image-container golden-glow logo-reveal-container"
              onClick={handleLogoClick}
              style={{ 
                background: isFlipped 
                  ? "linear-gradient(to bottom, #0d1520 0%, #1a2332 100%)"
                  : "linear-gradient(to bottom, #ffffff 0%, #d3d3d3 100%)",
                borderColor: isFlipped
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(212, 175, 55, 0.3)",
                borderRadius: "16px",
                border: "2px solid",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.8)",
                cursor: "pointer",
                transition: "background 0.8s ease, border-color 0.8s ease",
              }}
            >
              <motion.img 
                key={`${isFoundation}-${isFlipped}`}
                src={getLogoUrl()}
                alt={`${isFoundation ? 'IPMI Foundation' : 'IPMI'} 50th Anniversary Logo`}
                className="logo-image logo-3d"
                initial={{ opacity: 0, scale: 0.6, y: 30 }}
                animate={{
                  opacity: isInView ? 1 : 0,
                  scale: isInView ? 1.15 : 0.6,
                  y: isInView ? 0 : 30,
                  rotateY: isFlipped ? 360 : 0
                }}
                transition={{ 
                  opacity: { duration: 2, delay: 0.9, ease: "easeOut" },
                  scale: { duration: 2, delay: 0.9, ease: "easeOut" },
                  y: { duration: 2, delay: 0.9, ease: "easeOut" },
                  rotateY: { duration: 2.5, ease: "easeInOut" }
                }}
                style={{ 
                  transformStyle: "preserve-3d",
                }}
              />
            </div>
            <div className="logo-description">
              <p>The golden ribbon connecting the 5 and 0 represents IPMI&apos;s long-standing role in bringing people together. It reflects our legacy of <span className="highlight-gold">connection</span> and marks the start of our <strong className="highlight-gold">Golden Jubilee</strong> celebration.</p>
              <motion.p 
                className="logo-click-hint"
                initial={{ opacity: 0 }}
                animate={{ opacity: isInView ? 0.6 : 0 }}
                transition={{ duration: 1, delay: 3 }}
              >
                <span className="click-icon">👆</span>
                Click the logo to reveal its alternate color
              </motion.p>
            </div>
          </motion.div>
        </div>

        {/* Design Elements Explanation */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="design-elements"
        >
          <h3 className="elements-title">Design Elements</h3>
          <div className="elements-grid">
            <div className="element-card">
              <div className="element-icon gold">50</div>
              <h4>Golden Anniversary</h4>
              <p>Symbolizing 50 years of excellence, leadership, and precious metals expertise</p>
            </div>
            <div className="element-card">
              <div className="element-icon ribbon">
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <path d="M5 20 Q 12 10, 20 20 T 35 20" stroke="currentColor" strokeWidth="3" fill="none" />
                </svg>
              </div>
              <h4>The Connection Ribbon</h4>
              <p>Linking the 5 and 0, inspired by our iconic networking ring, representing unity</p>
            </div>
            <div className="element-card">
              <div className="element-icon legacy">IPMI</div>
              <h4>Heritage Preserved</h4>
              <p>Maintaining our core identity while celebrating this milestone achievement</p>
            </div>
          </div>
        </motion.div>

        {/* Tagline */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
          className="tagline-container"
        >
          <p className="tagline">Connect <span className="silver-dot">•</span> Learn <span className="silver-dot">•</span> Build Relationships</p>
          <p className="tagline-sub"><span className="highlight-gold-bold">Connecting</span> you to the world of precious metals for 50 years</p>
        </motion.div>
      </div>
    </section>
  );
};

export default LogoReveal;
